import { formatDateDDMMYY, formatDateTimeDDMMYY } from '../../utils/dateFormat';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import DnsIcon from '@mui/icons-material/Dns';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HubIcon from '@mui/icons-material/Hub';

import { AdminReportsSummary, getAdminReportsSummary } from '../../services/adminService';
import { useSnackbar } from '../../contexts/SnackbarContext';

const cardSx = {
  borderRadius: 0,
  border: '0.5px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
} as const;

const labelSx = {
  fontSize: '0.64rem',
  fontWeight: 800,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.disabled',
};

const monoSx = {
  fontFamily: "'DM Mono', monospace",
  fontSize: '0.78rem',
  fontWeight: 800,
};

const formatMoney = (currency: string, amount: number) => `${currency || 'USD'} ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const formatDate = (value?: string) => formatDateTimeDDMMYY(value, 'N/A');

const Metric = ({ label, value, helper, color }: { label: string; value: React.ReactNode; helper?: string; color: string }) => (
  <Box sx={{ minWidth: 132, borderLeft: `3px solid ${color}`, pl: 1.25 }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
    {helper && <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{helper}</Typography>}
  </Box>
);

export const AdminReportsPage: React.FC = () => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const [report, setReport] = useState<AdminReportsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getAdminReportsSummary();
      setReport(data);
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to load reports', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportJson = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `streamy-admin-report-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !report) {
    return <Box sx={{ p: 8, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 1700, mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Stack direction="row" spacing={2.3} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center">
              <AssessmentIcon sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 900 }}>Reports</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Operational, financial, and resource snapshot</Typography>
              </Box>
            </Stack>
            {report && (
              <>
                <Metric label="Revenue" value={formatMoney(report.billing.currency, report.billing.revenueThisMonth)} helper="this month" color={theme.palette.success.main} />
                <Metric label="Users" value={report.users.total} helper={`${report.users.active} active`} color={theme.palette.primary.main} />
                <Metric label="Channels" value={report.channels.total} helper={`${report.channels.active} active`} color={theme.palette.info.main} />
                <Metric label="Outputs" value={report.outputs.total} helper={`${report.outputs.managed} managed`} color={theme.palette.secondary.main} />
                <Metric label="Issues" value={report.billing.failedPayments + report.billing.openInvoices} helper="failed payments + open invoices" color={(report.billing.failedPayments + report.billing.openInvoices) ? theme.palette.error.main : theme.palette.success.main} />
              </>
            )}
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title="Refresh report"><span><IconButton size="small" onClick={load} disabled={loading} sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'divider', width: 34, height: 34 }}>{loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}</IconButton></span></Tooltip>
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportJson} disabled={!report} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Export JSON</Button>
          </Stack>
        </Stack>
      </Paper>

      {!report ? <Alert severity="warning" sx={{ borderRadius: 0 }}>No report data available.</Alert> : (
        <Stack spacing={1.5}>
          <Alert severity="info" sx={{ borderRadius: 0 }}>Generated at {formatDate(report.generatedAt)}. Month starts {formatDateDDMMYY(report.range.monthStart)}.</Alert>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            {[
              { title: 'User Growth', icon: <PeopleIcon />, rows: [['Total users', report.users.total], ['Active users', report.users.active], ['Disabled users', report.users.disabled], ['New this month', report.users.newThisMonth]] },
              { title: 'Channel Estate', icon: <DnsIcon />, rows: [['Total channels', report.channels.total], ['Active channels', report.channels.active], ['Inactive channels', Math.max(0, report.channels.total - report.channels.active)]] },
              { title: 'Finance', icon: <ReceiptLongIcon />, rows: [['Revenue this month', formatMoney(report.billing.currency, report.billing.revenueThisMonth)], ['Pending payments', report.billing.pendingPayments], ['Failed payments', report.billing.failedPayments], ['Open invoices', report.billing.openInvoices]] },
              { title: 'Output Inventory', icon: <HubIcon />, rows: [['Direct', report.outputs.direct], ['Remapped', report.outputs.managed], ['HLS', report.outputs.hls], ['HLS+', report.outputs.hlsPlus]] },
            ].map((section) => (
              <Paper key={section.title} elevation={0} sx={{ ...cardSx, p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                  <Box sx={{ color: 'text.secondary' }}>{section.icon}</Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 900 }}>{section.title}</Typography>
                </Stack>
                <Stack spacing={0.8}>
                  {section.rows.map(([label, value]) => (
                    <Stack key={String(label)} direction="row" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>{label}</Typography>
                      <Typography sx={monoSx}>{value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Box>

          <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 1.25, borderBottom: '0.5px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon sx={{ color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 900 }}>Report Summary</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={labelSx}>Area</TableCell>
                    <TableCell sx={labelSx}>Metric</TableCell>
                    <TableCell sx={labelSx}>Value</TableCell>
                    <TableCell sx={labelSx}>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    ['Finance', 'Monthly revenue', formatMoney(report.billing.currency, report.billing.revenueThisMonth), 'Paid payments in current month'],
                    ['Finance', 'Open receivables', report.billing.openInvoices, 'Issued / partial / overdue invoices'],
                    ['Users', 'New this month', report.users.newThisMonth, 'Created since month start'],
                    ['Users', 'Disabled', report.users.disabled, 'Banned / disabled accounts'],
                    ['Channels', 'Active ratio', `${report.channels.active}/${report.channels.total}`, 'Active channel count'],
                    ['Outputs', 'Remapped slots', report.outputs.managed, 'Configured non-copy outputs'],
                    ['Outputs', 'HLS+', report.outputs.hlsPlus, 'SCTE-35-capable HLS outputs'],
                  ].map((row) => (
                    <TableRow key={`${row[0]}-${row[1]}`} hover>
                      <TableCell sx={{ fontSize: '0.78rem', fontWeight: 800 }}>{row[0]}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{row[1]}</TableCell>
                      <TableCell sx={monoSx}>{row[2]}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{row[3]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}
    </Box>
  );
};
